import { UseFormReturn, useWatch } from 'react-hook-form';
import { TextInputField } from '@brighte/form-components';
import { RepaymentAccountType } from '@brighte/validation';
import { datadogRum } from '@datadog/browser-rum';
import { useBsbLookupQuery } from '@graphql/index';
import { Box } from '@spark-web/box';
import { ExclamationCircleIcon } from '@spark-web/icon';
import { Spinner } from '@spark-web/spinner';
import { Text } from '@spark-web/text';
import { InputAdornment } from '@spark-web/text-input';
import { LABELS, REPAYMENT_ACCOUNT } from '@utils';

type BsbInputProps = Pick<UseFormReturn<RepaymentAccountType>, 'control'>;

function BsbInput({ control }: BsbInputProps) {
  const bsbNumber = useWatch({
    control,
    name: 'payment.directDebit.bsb',
  });

  const { isError, isFetching } = useBsbLookupQuery(
    { bsbNumber: bsbNumber ?? '' },
    {
      enabled: !!bsbNumber && /^\d{6}$/.test(bsbNumber), // only fetch if bsbNumber is 6 digits
      retry: false,
      onError: error => {
        datadogRum.addAction('Failed to fetch BSB lookup data', { message: String(error) });
      },
    },
  );

  return (
    <Box>
      <TextInputField name="payment.directDebit.bsb" control={control} label={LABELS.BSB}>
        {bsbNumber && isFetching ? (
          <InputAdornment placement="end">
            <Spinner />
          </InputAdornment>
        ) : null}
      </TextInputField>
      {isError && (
        <Box display="flex" gap="xsmall" marginY="medium">
          <ExclamationCircleIcon size="xxsmall" tone="caution" />
          <Text tone="caution" size="small">
            {REPAYMENT_ACCOUNT.BSB_INVALID}
          </Text>
        </Box>
      )}
    </Box>
  );
}

export default BsbInput;
